import { useTranslation } from 'react-i18next'
import { FormState } from 'final-form'
import { EventTypes } from 'types/events'
import { SoftService } from 'types/callstore'
import { FormattedCaller } from 'components/fields/Submit'
import useCreateServiceEvent from 'hooks/events/useCreateServiceEvent'
import useUpdateTicketCharges from 'hooks/tickets/useUpdateTicketCharges'
import useAAAStandardServices from './useAAAStandardServices'
import { useServiceProxy } from 'hooks/kong'
import { StandardDispatchResponse, StandardServiceCall } from 'types/aaa'
import { useDispatch } from 'react-redux'
import { setCurrentEvent } from 'redux/currentEvent/currentEventSlice'
import { pruneAAADispatch } from 'utils/aaa'
import { useFleetVehicleVEMNote } from 'hooks/kong/useFleetVehicleVEMNote'
import { IncomingStandardServiceCall } from 'types/api'
import { AxiosResponse } from 'axios'
import { deepMapSanitize } from 'utils/deepMap'
import { ProviderNames } from 'hooks/dispatch/utils'

export const useCreateSoftService = (eventType: EventTypes) => {
  const { t } = useTranslation()
  const createServiceRequest = useAAAStandardServices([eventType])
  const createServiceEvent = useCreateServiceEvent({
    eventType,
  })
  const serviceProxy = useServiceProxy()
  const dispatch = useDispatch()
  const updateTicketCharges = useUpdateTicketCharges()
  const createVEMNote = useFleetVehicleVEMNote()

  return async function (state: FormState<SoftService>, caller: FormattedCaller) {
    try {
      const service = state.values as SoftService
      const serviceRequests = createServiceRequest(service, caller)
      // Create AAA Dispatch
      const { data: dispatchResponse } = await serviceProxy<StandardDispatchResponse>(
        'post',
        `/serviceproxy/aaa/dispatch/`,
        {},
        serviceRequests[0], // only one for soft services
      )

      const callKey = dispatchResponse.callKey

      const url = `/serviceproxy/aaa/dispatch/${callKey}`
      const inc = await serviceProxy<IncomingStandardServiceCall>('get', url)
      const { data: dispatchData } = { ...inc, data: deepMapSanitize(inc.data) } as AxiosResponse<StandardServiceCall>

      // Create DynamoDB Event
      if (dispatchData) {
        // Ticket charges
        await updateTicketCharges(eventType, ProviderNames.AAA)
        await createVEMNote(eventType)

        if (dispatchData) {
          const prunedDispatch = pruneAAADispatch(dispatchData)
          const event = await createServiceEvent(
            {
              ...service,
              dispatch: [prunedDispatch],
              provider: 'AAA',
              customerOptIn: caller.optIn || dispatchData?.customer?.contact?.phones?.[0].smsOptIn || false,
            },
            dispatchData.callKey,
          )
          dispatch(setCurrentEvent({ eventType, newEvent: event.data }))
        }
      }
    } catch (e) {
      console.error(e)
      return t('error.aaa')
    }
  }
}

export default useCreateSoftService
